import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { useDispatch, useSelector } from 'react-redux';
import { Dropdown } from 'primereact/dropdown';
import { classNames } from 'primereact/utils';
import appActions from '../../../appActions';
import { dateTimeTemplate, isPermissionCodeValid, yesNoOptions } from '../../../utils/mics';
import { Toast } from 'primereact/toast';
import moment from 'moment';
import { InputTextarea } from 'primereact/inputtextarea';

const SysAppLoggerDetail  = ({ pageId }) => {
    
    // const readAccess = isPermissionCodeValid(`${pageId}-R`);
    // const writeAccess = isPermissionCodeValid(`${pageId}-W`);
    // const deleteAccess = isPermissionCodeValid(`${pageId}-D`)

    const toast = useRef(null)

    const { state } = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const sysAppLogger = useSelector((state) => state.appLogger.sysAppLogger);

    const [record, setRecord] = useState({});
    const [isEditMode, setIsEditMode] = useState(false);
    const [isViewMode, setIsViewMode] = useState(false);

    useEffect(() => {
        if (!state) return;
        const { data, isView, isEdit } = state;
        setRecord(data || {});
        setIsViewMode(isView || false);
        setIsEditMode(isEdit || false);
    }, [state]);

    const handleSubmit = (data) => {
        if (data.id) {
            dispatch(appActions.SYSAPPLOGGER_UPDATE_REQUEST(data));
        } else {
            dispatch(appActions.SYSAPPLOGGER_CREATE_REQUEST(data));
        }
      
    };

    const handleRedirect = useCallback(() => {
        dispatch(appActions.SYSAPPLOGGER_RESET_REQUEST());
        navigate('/ui/system-app-logger', { replace: true });
    }, [dispatch, navigate]);

    useEffect(() => {
        if (sysAppLogger?.action === 'create' || sysAppLogger?.action === 'update') {
            handleRedirect();
        }
    }, [sysAppLogger, handleRedirect]);

    const formik = useFormik({
        initialValues: {
            id : record?.id || '',
            fileId : record?.fileId || '',
            fileName : record?.fileName || '',
            errorTag : record?.errorTag || '',
            errorTagValue : record?.errorTagValue || '',
            storeId : record?.storeId || '',
            errorCode : record?.errorCode || '',
            errorMsg : record?.errorMsg || '',
            alertGroup : record?.alertGroup || '',
            alertDateTime : record?.alertDateTime || '',
            alertPriority : record?.alertPriority || null,
            alertProcessed : record?.alertProcessed || '',
            logText : record?.logText || '',
            createdOn : dateTimeTemplate(record?.createdOn) || '',
            lastUpdated : dateTimeTemplate(record?.lastUpdated) || '',
            lastUpdatedBy : record?.lastUpdatedBy || '',
        },
        enableReinitialize: true,
        validate: (data) => {
            let errors = {};
            if (!data.fileId) {
                errors.fileId = 'fileId is required.';
            }
            if (!data.errorTag) {
                errors.errorTag = 'ErrorTag is required.';
            }
            if (!data.errorCode) {
                errors.errorCode = 'Error Code is required.';
            }
            return errors;
        },
        onSubmit: (values) => {
            const data = {
                ...values,
                alertDateTime :  values.alertDateTime ? moment(values.alertDateTime).format('YYYY-MM-DD') : '',
            }

            if (isEditMode) {
                data['id'] = record.id;
            }
            handleSubmit(data);
        },
    });

    const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);

    const getFormErrorMessage = (name) => isFormFieldValid(name) &&  <small className="p-error"> {formik.errors[name]} </small> 

    const renderFormArea = () => {
        return (
            <div className="p-fluid formgrid grid br-2 bg-white p-2 py-2 border-round-xl shadow-1">

                {(isEditMode || isViewMode) && (
                    <div className="col-4 mb-3">
                        <label htmlFor="id">Id</label>
                        <InputText id="id" name="id" value={formik.values.id} onChange={formik.handleChange} disabled />
                    </div>
                )}

                <div className="col-4 mb-3">
                    <label htmlFor="storeId">Store Id</label>
                    <InputText id="storeId" name="storeId" value={formik.values.storeId} onChange={formik.handleChange} />
                </div>

                <div className="col-4 mb-3">
                    <label htmlFor="fileName">File Name</label>
                    <InputText id="fileName" name="fileName" value={formik.values.fileName} onChange={formik.handleChange} 
                        className={classNames({ 'p-invalid': isFormFieldValid('fileName') })} readOnly={isViewMode} />
                    {getFormErrorMessage('fileName')}
                </div>

                <div className="col-4 mb-3">
                    <label htmlFor="errorCode">Error Code</label>
                    <InputText id="errorCode" name="errorCode" value={formik.values.errorCode} onChange={formik.handleChange} readOnly={isViewMode} />
                </div>

                <div className="col-4 mb-3">
                    <label htmlFor="errorMsg">Error Message</label>
                    <InputText id="errorMsg" name="errorMsg" value={formik.values.errorMsg} onChange={formik.handleChange} readOnly={isViewMode} />
                </div>

                <div className="col-4 mb-3">
                    <label htmlFor="errorTag">Error Tag</label>
                    <InputText id="errorTag" name="errorTag" value={formik.values.errorTag} onChange={formik.handleChange} readOnly={isViewMode} />
                </div>

                <div className="col-4 mb-3">
                    <label htmlFor="errorTagValue">Error Tag Value</label>
                    <InputText id="errorTagValue" name="errorTagValue" value={formik.values.errorTagValue} onChange={formik.handleChange} readOnly={isViewMode} />
                </div>

                <div className="col-4 mb-3">
                    <label htmlFor="alertGroup">Alert Group</label>
                    <InputText id="alertGroup" name="alertGroup" value={formik.values.alertGroup} onChange={formik.handleChange} readOnly={isViewMode} />
                </div>

                <div className="col-4 mb-3">
                    <label htmlFor="alertDateTime">Alert Date Time</label>
                    <InputText id="alertDateTime" name="alertDateTime" value={formik.values.alertDateTime} onChange={formik.handleChange} readOnly={isViewMode} />
                </div>

                <div className="col-4 mb-3">
                    <label htmlFor="alertPriority">Alert Priority</label>
                    <InputText id="alertPriority" name="alertPriority" value={formik.values.alertPriority} onChange={formik.handleChange} readOnly={isViewMode} />
                </div>

                <div className="col-4 mb-3">
                    <label htmlFor="alertProcessed">Alert Processed</label>
                    <Dropdown filter showClear value={formik.values.alertProcessed} onChange={formik.handleChange} options={yesNoOptions} id="alertProcessed" name="alertProcessed" optionLabel="name" optionValue='code' disabled={isViewMode} />
                </div>
                
                {(isEditMode || isViewMode) && (
                    <>
                        <div className="col-4 mb-3">
                            <label htmlFor="createdOn">Created On</label>
                            <InputText id="createdOn" name="createdOn" value={formik.values.createdOn} onChange={formik.handleChange} disabled/>
                        </div>
                        <div className="col-4 mb-3">
                            <label htmlFor="lastUpdated">Last Updated</label>
                            <InputText id="lastUpdated" name="lastUpdated" value={formik.values.lastUpdated} onChange={formik.handleChange} disabled />
                        </div>
                        <div className="col-4 mb-3">
                            <label htmlFor="lastUpdatedBy">Last Updated By</label>
                            <InputText id="lastUpdatedBy" name="lastUpdatedBy" value={formik.values.lastUpdatedBy} onChange={formik.handleChange} disabled />
                        </div>
                    </>
                )}

                <div className="col-12 mb-3">
                    <label htmlFor="logText">Log Text</label>
                    <InputTextarea autoResize id="logText" name="logText" value={formik.values.logText} onChange={formik.handleChange} rows={3} cols={60} readOnly />
                </div>

               
            </div>
        );
    };

    const renderMainButton = () => {
        return (
            <>
                {!isViewMode &&
                    <Button label={isEditMode ? 'Update' : 'Save'} 
                    severity="success" outlined 
                    data-pr-tooltip={isEditMode ? 'Update' : 'Save'}
                    className="p-button-success tp" icon="pi pi-save" type="submit" />
                }
                <Button 
                    label={isViewMode ? 'Back' : 'Cancel'}
                    icon={isViewMode ? 'pi pi-arrow-left' : 'pi pi-times'}
                    className="p-button-secondary tp" type="button"
                    severity="secondary" outlined
                     data-pr-tooltip={isViewMode ? 'Back' : 'Cancel'}
                    onClick={() => navigate('/ui/system-app-logger')}
                />
            </>
        );
    };

    return (
        <>
            <Toast ref={toast} />
            <form onSubmit={formik.handleSubmit}>
                <div className="form_height px-2 mt-2">
                    <div className="d-flex flex-row gap-10 align-items-center mb-1">
                        <h2 className="page-header mr-auto"> {isEditMode ? 'Edit System App Logger' : 'System App Logger Details'} </h2>
                        {renderMainButton()}
                    </div>
                    <div className="grid px-2">
                        <div className="col-12">{renderFormArea()}</div>
                       
                    </div>
                </div>
            </form>
        </>
    );
};

export default SysAppLoggerDetail;
